<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import WootButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import { format } from 'date-fns';

const props = defineProps({
  campaignDetails: {
    type: Array,
    required: true,
  },
  detailsMeta: {
    type: Object,
    required: true,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  selectedDetailIds: {
    type: Object, // Set
    required: true,
  },
  isAllSelected: {
    type: Boolean,
    default: false,
  },
  currentPage: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits([
  'toggle-select-all',
  'toggle-selection',
  'view-conversation',
  'page-change',
]);

const { t } = useI18n();

const perPage = computed(() => props.detailsMeta.per_page || 25);
const isIndeterminate = computed(() => {
  if (!props.campaignDetails.length) return false;

  // Calculate selected count on current page
  const idsOnPage = new Set(props.campaignDetails.map(detail => detail.id));
  let countOnPage = 0;
  props.selectedDetailIds.forEach(id => {
    if (idsOnPage.has(id)) countOnPage++;
  });

  return countOnPage > 0 && countOnPage < props.campaignDetails.length;
});

const formatTimestamp = timestamp => {
  if (!timestamp || timestamp === 0) return t('CAMPAIGN.DETAILS.NO_DATA');
  try {
    const date = new Date(timestamp * 1000);
    if (isNaN(date.getTime())) return t('CAMPAIGN.DETAILS.NO_DATA');
    return format(date, 'dd MMM yyyy, HH:mm');
  } catch (error) {
    return t('CAMPAIGN.DETAILS.NO_DATA');
  }
};

const formatStatus = status => {
  if (!status) return '';
  if (status === 'awaiting_reply') return t('CAMPAIGN.DETAILS.STATUS.NO_REPLY');
  return status.replace(/_/g, ' ');
};

const statusBadgeClass = status => {
  const classes = {
    delivered: 'bg-n-iris-3 text-n-iris-11',
    sent: 'bg-n-blue-3 text-n-blue-11',
    pending: 'bg-n-amber-3 text-n-amber-11',
    failed: 'bg-n-ruby-3 text-n-ruby-11',
  };
  return classes[status] || 'bg-n-alpha-black2 text-n-slate-12';
};

const readBadgeClass = status => {
  return status === 'read'
    ? 'bg-n-blue-3 text-n-blue-11'
    : 'bg-n-alpha-black2 text-n-slate-12';
};

const replyBadgeClass = status => {
  return status === 'replied'
    ? 'bg-n-teal-3 text-n-teal-11'
    : 'bg-n-alpha-black2 text-n-slate-12';
};
</script>

<template>
  <div
    class="overflow-hidden border rounded-xl border-n-alpha-black2 bg-n-solid-1"
  >
    <div v-if="isLoading" class="flex items-center justify-center py-16">
      <Spinner />
    </div>
    <div v-else-if="!campaignDetails.length" class="p-8 text-center">
      <p class="text-sm text-n-slate-11">
        {{ t('CAMPAIGN.DETAILS.EMPTY') }}
      </p>
    </div>
    <div v-else>
      <table class="w-full text-sm">
        <thead class="text-xs text-left uppercase border-b bg-n-alpha-black2">
          <tr>
            <th class="px-4 py-3 w-12">
              <Checkbox
                :model-value="isAllSelected"
                :indeterminate="isIndeterminate"
                @change="$emit('toggle-select-all')"
              />
            </th>
            <th class="px-4 py-3">
              {{ t('CAMPAIGN.DETAILS.COLUMNS.CONTACT') }}
            </th>
            <th class="px-4 py-3">
              {{ t('CAMPAIGN.DETAILS.COLUMNS.DELIVERY') }}
            </th>
            <th class="px-4 py-3">
              {{ t('CAMPAIGN.DETAILS.COLUMNS.TIMINGS') }}
            </th>
            <th class="px-4 py-3">
              {{ t('CAMPAIGN.DETAILS.COLUMNS.ERROR') }}
            </th>
            <th class="px-4 py-3">
              {{ t('CAMPAIGN.DETAILS.COLUMNS.ACTIONS') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="detail in campaignDetails"
            :key="detail.id"
            class="border-b last:border-b-0"
          >
            <td class="px-4 py-4 align-top">
              <Checkbox
                :model-value="selectedDetailIds.has(detail.id)"
                @change="$emit('toggle-selection', detail.id)"
              />
            </td>
            <td class="px-4 py-4 align-top">
              <div class="flex items-start gap-3">
                <Avatar
                  :name="detail.contact.name"
                  :src="detail.contact.thumbnail"
                  :size="32"
                  rounded-full
                />
                <button
                  class="text-left group w-full"
                  @click="$emit('view-conversation', detail.conversation_id)"
                >
                  <span
                    class="block m-0 font-medium text-n-slate-12 truncate group-hover:text-n-brand group-hover:underline"
                  >
                    {{
                      detail.contact.name ||
                      t('CAMPAIGN.DETAILS.COLUMNS.NO_NAME')
                    }}
                  </span>
                  <span class="block m-0 text-n-slate-11 truncate">
                    {{
                      detail.contact.email ||
                      detail.contact.phone_number ||
                      t('CAMPAIGN.DETAILS.NO_DATA')
                    }}
                  </span>
                </button>
              </div>
            </td>
            <td class="px-4 py-4 align-top">
              <div class="flex flex-col gap-2">
                <span
                  class="inline-flex items-center justify-center w-fit px-2.5 py-1 text-xs font-medium rounded-full capitalize"
                  :class="statusBadgeClass(detail.delivery_status)"
                >
                  {{ detail.delivery_status }}
                </span>
                <span
                  class="inline-flex items-center justify-center w-fit px-2.5 py-1 text-xs font-medium rounded-full capitalize"
                  :class="readBadgeClass(detail.read_status)"
                >
                  {{ formatStatus(detail.read_status) }}
                </span>
                <span
                  class="inline-flex items-center justify-center w-fit px-2.5 py-1 text-xs font-medium rounded-full capitalize"
                  :class="replyBadgeClass(detail.reply_status)"
                >
                  {{ formatStatus(detail.reply_status) }}
                </span>
              </div>
            </td>
            <td class="px-4 py-4 align-top">
              <div class="text-xs text-n-slate-11 space-y-1">
                <p class="m-0">
                  {{
                    t('CAMPAIGN.DETAILS.TIMESTAMP.SENT', {
                      value: formatTimestamp(detail.sent_at),
                    })
                  }}
                </p>
                <p class="m-0">
                  {{
                    t('CAMPAIGN.DETAILS.TIMESTAMP.DELIVERED', {
                      value: formatTimestamp(detail.delivered_at),
                    })
                  }}
                </p>
                <p class="m-0">
                  {{
                    t('CAMPAIGN.DETAILS.TIMESTAMP.READ', {
                      value: formatTimestamp(detail.read_at),
                    })
                  }}
                </p>
                <p class="m-0">
                  {{
                    t('CAMPAIGN.DETAILS.TIMESTAMP.REPLIED', {
                      value: formatTimestamp(detail.replied_at),
                    })
                  }}
                </p>
              </div>
            </td>
            <td class="px-4 py-4 align-top">
              <p class="text-xs text-n-ruby-11">
                {{ detail.error_code || t('CAMPAIGN.DETAILS.NO_DATA') }}
              </p>
              <p class="text-xs text-n-slate-11">
                {{ detail.error_message || t('CAMPAIGN.DETAILS.NO_DATA') }}
              </p>
            </td>
            <td class="px-4 py-4 align-top">
              <WootButton
                v-if="detail.conversation_id"
                size="xs"
                variant="ghost"
                icon="i-lucide-message-square"
                :label="t('CAMPAIGN.DETAILS.ACTIONS.VIEW_CONVERSATION')"
                @click="$emit('view-conversation', detail.conversation_id)"
              />
              <span v-else class="text-xs text-n-slate-11">{{
                t('CAMPAIGN.DETAILS.NO_DATA')
              }}</span>
            </td>
          </tr>
        </tbody>
      </table>

      <PaginationFooter
        v-if="detailsMeta.total"
        class="border-t border-n-alpha-black2"
        :total-items="detailsMeta.total"
        :items-per-page="perPage"
        :current-page="currentPage"
        @update:current-page="$emit('page-change', $event)"
      />
    </div>
  </div>
</template>
